/**
 * StreakHeroCard — visual streak panel for the Profile page. Works for
 * both reader and writer (same DB shape: `{ current, longest, active_days_90d }`)
 * via SmarterHubStats `/api/stats/profile?role=…`.
 *
 * The flame size + glow scales with the streak so power-users get a
 * visibly hotter card.
 */
import React, { useEffect, useState } from 'react';
import { Flame, Trophy, Shield, Coins, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const StreakHeroCard = ({ role = 'reader' }) => {
  const [streak, setStreak] = useState(null);
  const [savers, setSavers] = useState(0);
  const [buying, setBuying] = useState(false);

  const refresh = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/stats/profile?role=${role}`, {
        headers: { 'X-TZ-Offset-Min': String(new Date().getTimezoneOffset()) },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setStreak(d.streak || { current: 0, longest: 0 });
    } catch {
      setStreak({ current: 0, longest: 0 });
    }
    try {
      const inv = await authFetch(`${BACKEND_URL}/api/shop/inventory`);
      if (inv.ok) {
        const data = await inv.json();
        const rows = (data.inventory || []).filter(
          (i) => i.category === 'streak' && !i.consumed_at,
        );
        setSavers(rows.length);
      }
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await refresh();
      if (!alive) return;
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const buyStreakSaver = async () => {
    if (buying) return;
    setBuying(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/shop/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: 'house:freeze_1d' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Purchase failed');
      toast.success('Streak Saver added — covers 1 missed day');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Could not buy Streak Saver');
    } finally {
      setBuying(false);
    }
  };

  if (!streak) return null;
  const cur = streak.current || 0;
  const best = Math.max(streak.longest || 0, cur);

  // Heat tiers — drive the colour stops, flame size, and shadow strength.
  const tier =
    cur >= 30 ? 'inferno' :
    cur >= 14 ? 'blaze'   :
    cur >= 7  ? 'roar'    :
    cur >= 3  ? 'spark'   : 'dormant';

  const palette = {
    dormant: { bg: 'from-slate-800 to-slate-900', flame: 'text-slate-500', accent: 'text-slate-400', glow: 'shadow-slate-900/30', emoji: '✨', label: 'Start your streak' },
    spark:   { bg: 'from-amber-900/60 to-slate-900', flame: 'text-amber-300', accent: 'text-amber-200', glow: 'shadow-amber-500/30', emoji: '🔥', label: 'Building heat' },
    roar:    { bg: 'from-orange-700/70 to-rose-900/40', flame: 'text-orange-300', accent: 'text-orange-200', glow: 'shadow-orange-500/40', emoji: '🔥', label: 'On a roll' },
    blaze:   { bg: 'from-rose-600/70 to-fuchsia-900/40', flame: 'text-rose-200', accent: 'text-rose-100', glow: 'shadow-rose-500/50', emoji: '💥', label: 'Blazing' },
    inferno: { bg: 'from-fuchsia-600/80 to-amber-500/40', flame: 'text-amber-100', accent: 'text-amber-100', glow: 'shadow-fuchsia-500/60', emoji: '🌋', label: 'Inferno' },
  }[tier];

  const role_label = role === 'writer' ? 'Writing' : 'Reading';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${palette.bg} border border-white/10 p-5 sm:p-6 shadow-xl ${palette.glow}`}
      data-testid={`streak-hero-${role}`}
    >
      {/* Decorative pulsing ring behind the flame */}
      {cur > 0 && (
        <span
          className={`pointer-events-none absolute -top-6 -right-6 w-32 h-32 rounded-full bg-current ${palette.flame} opacity-10 blur-2xl animate-pulse`}
          aria-hidden="true"
        />
      )}

      <div className="relative flex items-center gap-4">
        <div className="relative">
          <Flame
            className={`${palette.flame} ${
              tier === 'inferno' ? 'w-16 h-16'
              : tier === 'blaze' ? 'w-14 h-14'
              : tier === 'roar'  ? 'w-12 h-12'
              : 'w-10 h-10'
            } drop-shadow-[0_0_18px_currentColor]`}
            strokeWidth={1.6}
            aria-hidden="true"
          />
          {cur > 0 && (
            <span
              className="absolute -bottom-1 -right-2 text-xl"
              aria-hidden="true"
            >
              {palette.emoji}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-widest font-bold text-white/70">
            {role_label} streak
          </p>
          <div className="flex items-baseline gap-2">
            <span
              className={`text-4xl sm:text-5xl font-black tabular-nums ${palette.accent} drop-shadow`}
              data-testid={`streak-hero-${role}-current`}
            >
              {cur}
            </span>
            <span className="text-base sm:text-lg font-bold text-white/80">
              {cur === 1 ? 'day' : 'days'}
            </span>
          </div>
          <p className="text-[11px] sm:text-xs text-white/70 font-semibold mt-0.5">
            {palette.label}
          </p>
        </div>

        <div className="shrink-0 text-right">
          <span className="text-[10px] uppercase tracking-widest font-bold text-white/60 inline-flex items-center gap-1">
            <Trophy className="w-3 h-3" /> Best
          </span>
          <p
            className="text-2xl sm:text-3xl font-black tabular-nums text-white"
            data-testid={`streak-hero-${role}-best`}
          >
            {best}
          </p>
        </div>
      </div>

      {/* 7-day flame chip row — visualises the last week so the user can
          see exactly how close they are to milestones. The chips are
          purely decorative; the source of truth is `cur`/`best`. */}
      <div className="relative mt-4 flex items-center gap-1.5">
        {Array.from({ length: 7 }).map((_, i) => {
          // Light up the rightmost N chips, where N = min(cur, 7).
          const active = i >= 7 - Math.min(cur, 7);
          return (
            <span
              key={i}
              className={`flex-1 h-2.5 rounded-full transition-all ${
                active
                  ? `bg-current ${palette.flame} shadow-[0_0_8px_currentColor]`
                  : 'bg-white/10'
              }`}
              aria-hidden="true"
            />
          );
        })}
      </div>
      <p className="relative mt-1.5 text-[10px] uppercase tracking-widest text-white/60 font-bold text-right">
        Last 7 days
      </p>

      {/* Streak Saver row — owned tokens + "Buy with coins" CTA. Saves
          your streak when you miss a day (consumed automatically by
          the streak engine on next visit). */}
      <div
        className="relative mt-4 flex items-center justify-between gap-3 rounded-xl bg-black/30 border border-white/10 p-3"
        data-testid={`streak-hero-${role}-savers`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Shield className={`w-5 h-5 shrink-0 ${savers > 0 ? 'text-emerald-300' : 'text-white/50'}`} />
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest font-bold text-white/70">
              Streak Savers
            </p>
            <p className="text-xs text-white/80 truncate">
              {savers > 0
                ? `${savers} owned — auto-uses if you miss a day`
                : 'Protect your streak from a missed day'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={buyStreakSaver}
          disabled={buying}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-white text-slate-900 hover:bg-amber-200 disabled:opacity-50 px-3 py-1.5 text-xs font-black focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 transition-colors"
          data-testid={`streak-hero-${role}-buy-saver`}
        >
          {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coins className="w-3.5 h-3.5 text-amber-500" />}
          {buying ? 'Buying…' : 'Buy · 50'}
        </button>
      </div>
    </div>
  );
};

export default StreakHeroCard;
